import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import produce from "immer";
import useConfig from "hooks/useConfig";
import { countRailcards } from "lib/utils";
import Select from "components/ui/Select";
import { Railcards, TPERailcards } from "services/RailcardsService";
import "styles/components/railcards-selector.scss";
import A11yElement from "components/helpers/A11yElement";
import { ReactComponent as PlusButtonIcon } from "static/images/plus-button.svg";
import { ReactComponent as MinusButtonIcon } from "static/images/minus-button.svg";
import { BRAND_CODES } from "../consts";

const HINT_TIMEOUT_MS = 2000;
let currentId = 0;

const RailcardsSelector = ({ passengers, railcards = [], onRailcardsChange }) => {
  const [showHint, setShowHint] = useState(false);
  const timerId = useRef(false); //now you can pass timer to another component
  const { disabledRailcards, brand } = useConfig();
  const isTPE = brand === BRAND_CODES.TP;
  const brandRailcards = isTPE ? TPERailcards : Railcards;

  useEffect(() => {
    return () => {
      clearInterval(timerId.current);
    };
  }, []);

  const validRailcards = disabledRailcards
    ? brandRailcards.filter(({ code }) => !disabledRailcards.includes(code))
    : brandRailcards;
  const numRailcards = countRailcards(railcards);
  const hasReachedLimit = numRailcards >= passengers;

  const onAddClick = () => {
    if (!hasReachedLimit) {
      const nextRailcards = produce(railcards, draft => {
        draft.push({
          quantity: 1,
          railcardCode: validRailcards[0].code,
          id: `${Date.now()}${currentId++}`
        });
      });
      onRailcardsChange(nextRailcards);
    } else {
      setShowHint(true);
      clearInterval(timerId.current);
      timerId.current = setTimeout(() => setShowHint(false), HINT_TIMEOUT_MS);
    }
  };

  const onDeleteClick = id => () => {
    const nextRailcards = produce(railcards, draft => {
      return draft.filter(railcard => railcard.id !== id);
    });

    setShowHint(false);
    clearInterval(timerId.current);

    onRailcardsChange(nextRailcards);
  };

  const onQuantityChange = id => quantity => {
    const nextRailcards = produce(railcards, draft => {
      let index = -1;

      railcards.forEach((draft, i) => {
        if (draft.id === id) {
          index = i;
        }
      });

      if (index >= 0) {
        draft[index]["quantity"] = parseInt(quantity);
      }
    });

    onRailcardsChange(nextRailcards);
  };

  const onCodeChange = id => railcardCode => {
    const nextRailcards = produce(railcards, draft => {
      let index = -1;

      railcards.forEach((draft, i) => {
        if (draft.id === id) {
          index = i;
        }
      });

      if (index >= 0) {
        draft[index]["railcardCode"] = railcardCode;
      }
    });

    onRailcardsChange(nextRailcards);
  };

  return (
    <>
      <A11yElement
        role="button"
        className={cn("otrl-jp__railcards-selector__button", {
          "otrl-jp__railcards-selector__button--disabled": hasReachedLimit
        })}
        onAction={onAddClick}
      >
        <PlusButtonIcon className={cn("otrl-jp__railcards-selector__button__icon")} aria-hidden />
        <span className="otrl-jp__railcards-selector__button__text">Add railcard</span>
      </A11yElement>
      {showHint && (
        <span className="otrl-jp__railcards-selector__button__hint" role="status">Add more passengers first</span>
      )}
      {railcards &&
        railcards.map(railcard => {
          const maxQuantityWithMultipleRailcards = Math.max(
            passengers - numRailcards + 1,
            railcard.quantity
          );
          const maxQuantityForSingleRailcard = Math.max(passengers, railcard.quantity);
          const maxQuantity =
            railcards.length > 1 ? maxQuantityWithMultipleRailcards : maxQuantityForSingleRailcard;
          return (
            <div key={railcard.id} className="otrl-jp__railcards-selector__form">
              <Select
                className="otrl-jp__railcards-selector__form__number"
                onChange={onQuantityChange(railcard.id)}
                value={railcard.quantity}
                aria-label="Quantity"
              >
                {Array.from({ length: maxQuantity }, (v, index) => index + 1).map(quantity => (
                  <option name="quantity" value={quantity} key={quantity}>
                    {quantity}
                  </option>
                ))}
              </Select>
              <Select
                className="otrl-jp__railcards-selector__form__name"
                onChange={onCodeChange(railcard.id)}
                value={railcard.railcardCode}
                aria-label="Railcard type"
              >
                {validRailcards.map(railcard => (
                  <option name="code" value={railcard.code} key={railcard.code}>
                    {railcard.name}
                  </option>
                ))}
              </Select>
              <A11yElement
                as="button"
                className="otrl-jp__railcards-selector__button__icon otrl-jp__button-unstyled"
                onAction={onDeleteClick(railcard.id)}
              >
                <MinusButtonIcon
                  className={cn("otrl-jp__railcards-selector__button__icon__sign")}
                  aria-label="Remove railcard"
                />
              </A11yElement>
            </div>
          );
        })}
    </>
  );
};

export default RailcardsSelector;
